import React, { useState, useEffect } from "react";
import { ThumbsUp, ThumbsDown } from "lucide-react";
import { useAppDispatch, useAppSelector } from "../../redux";
import { createVote, removeVote, clearVoteError } from "../../redux/Slices/reviewSlice";

export interface Vote {
  id: string;
  userId: string;
  voteType: "helpful" | "unhelpful";
  createdAt: Date;
}

export interface VoteDisplayButtonProps {
  reviewId: string;
  votes: Vote[];
  currentUserId?: string;
  className?: string;
}

const VoteDisplayButton: React.FC<VoteDisplayButtonProps> = ({
  reviewId,
  votes = [],
  currentUserId,
  className = ""
}) => {
  const dispatch = useAppDispatch();
  const { voteLoading, voteError } = useAppSelector((state) => state.reviews);
  const [userVote, setUserVote] = useState<"helpful" | "unhelpful" | null>(null);
  const [localVotes, setLocalVotes] = useState<Vote[]>(votes);

  // Update local votes when props change
  useEffect(() => {
    setLocalVotes(votes);
  }, [votes]);

  // Calculate vote counts from local votes
  const helpfulCount = localVotes.filter(vote => vote.voteType === "helpful").length;
  const unhelpfulCount = localVotes.filter(vote => vote.voteType === "unhelpful").length;

  // Find user's current vote
  useEffect(() => {
    console.log("VoteDisplayButton.tsx useEffect", { currentUserId, localVotes });
    if (currentUserId && localVotes.length > 0) {
      const userCurrentVote = localVotes.find(vote => vote.userId === currentUserId);
      setUserVote(userCurrentVote?.voteType || null);
    } else if (!currentUserId) {
      setUserVote(null);
    }
  }, [currentUserId, localVotes]);

  const handleVote = async (voteType: "helpful" | "unhelpful") => {
    if (voteLoading || !currentUserId) return;

    try {
      // Clear any previous errors
      dispatch(clearVoteError());
      
      // Optimistic update - add the vote locally
      const newVote: Vote = {
        id: `temp-${Date.now()}`, // Temporary ID
        userId: currentUserId,
        voteType: voteType === "helpful" ? "helpful" : "unhelpful",
        createdAt: new Date()
      };
      
      // Remove any existing vote from this user first
      const updatedVotes = localVotes.filter(vote => vote.userId !== currentUserId);
      // Add the new vote
      setLocalVotes([...updatedVotes, newVote]);
      
      // Convert vote type to API format
      const apiVoteType = voteType === "helpful" ? "helpful" : "not_helpful";
      
      const result = await dispatch(createVote({ 
        reviewId, 
        data: { voteType: apiVoteType } 
      })).unwrap();
      
      // Log the result for debugging
      console.log("Vote created successfully:", result);
    } catch (error) {
      console.error("Error submitting vote:", error);
      // Revert optimistic update on error
      setLocalVotes(votes);
    }
  };

  const handleRemoveVote = async () => {
    if (voteLoading || !currentUserId) return;

    try {
      // Clear any previous errors
      dispatch(clearVoteError());
      
      // Optimistic update - remove the vote locally
      const updatedVotes = localVotes.filter(vote => vote.userId !== currentUserId);
      setLocalVotes(updatedVotes);
      
      const result = await dispatch(removeVote(reviewId)).unwrap();
      
      // Log the result for debugging
      console.log("Vote removed successfully:", result);
    } catch (error) {
      console.error("Error removing vote:", error);
      // Revert optimistic update on error
      setLocalVotes(votes);
    }
  };

  const handleHelpfulClick = () => {
    if (userVote === "helpful") {
      // If user already voted helpful, remove the vote
      handleRemoveVote();
    } else {
      // Vote helpful
      handleVote("helpful");
    }
  };

  const handleUnhelpfulClick = () => {
    if (userVote === "unhelpful") {
      // If user already voted unhelpful, remove the vote
      handleRemoveVote();
    } else {
      // Vote unhelpful
      handleVote("unhelpful");
    }
  };

  return (
    <div className={`flex items-center gap-4 ${className}`}>
      {/* Helpful Button */}
      <button
        onClick={handleHelpfulClick}
        disabled={voteLoading || !currentUserId}
        className={`
          flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200
          ${userVote === "helpful" 
            ? "bg-green-100 text-green-700 border border-green-300" 
            : "bg-gray-100 text-gray-600 hover:bg-green-50 hover:text-green-600 border border-gray-200"
          }
          ${voteLoading || !currentUserId ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}
        `}
      >
        <ThumbsUp 
          className={`w-4 h-4 ${userVote === "helpful" ? "text-green-600" : "text-gray-500"}`} 
        />
        {helpfulCount > 0 && (
          <span className={`
            px-2 py-1 rounded-full text-xs font-semibold
            ${userVote === "helpful" ? "bg-green-200 text-green-800" : "bg-gray-200 text-gray-700"}
          `}>
            {helpfulCount}
          </span>
        )}
      </button>

      {/* Unhelpful Button */}
      <button
        onClick={handleUnhelpfulClick}
        disabled={voteLoading || !currentUserId}
        className={`
          flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200
          ${userVote === "unhelpful" 
            ? "bg-red-100 text-red-700 border border-red-300" 
            : "bg-gray-100 text-gray-600 hover:bg-red-50 hover:text-red-600 border border-gray-200"
          }
          ${voteLoading || !currentUserId ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}
        `}
      >
        <ThumbsDown 
          className={`w-4 h-4 ${userVote === "unhelpful" ? "text-red-600" : "text-gray-500"}`} 
        />
        {unhelpfulCount > 0 && (
          <span className={`
            px-2 py-1 rounded-full text-xs font-semibold
            ${userVote === "unhelpful" ? "bg-red-200 text-red-800" : "bg-gray-200 text-gray-700"}
          `}>
            {unhelpfulCount}
          </span>
        )}
      </button>

      {/* Loading indicator */}
      {voteLoading && (
        <div className="flex items-center text-gray-500 text-sm">
          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-500 mr-2"></div>
          Updating...
        </div>
      )}
      
      {/* Error display */}
      {voteError && (
        <div className="flex items-center text-red-500 text-sm">
          <span>{voteError}</span>
        </div>
      )}
    </div>
  );
};

export default VoteDisplayButton;


